import type {
  InteractiveSurveyGroupLoginRequest,
  InteractiveSurveyGroupLoginResponse,
  InteractiveSurveyLoginRequest,
  InteractiveSurveyLoginResponse,
} from "@/features/auth/types";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

/**
 * Interactive Survey Authentication API
 * Handles 4-digit passcode authentication for survey and survey-group access
 */
export const interactiveSurveyAuth = createApi({
  reducerPath: "interactiveSurveyAuthApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_SURVEY_SERVICE_BASE_URL}interactive`,
    prepareHeaders: (headers) => {
      headers.set("Content-Type", "application/json");

      // Include interactiveToken if available
      const interactiveToken = getInteractiveToken();
      headers.set("sec-fetch-site", "same-origin");
      headers.set("interactiveToken", interactiveToken || "");

      // Include surveyId if available
      const surveyId = getSurveyId();
      if (surveyId) {
        headers.set("X-Survey-ID", surveyId);
      }

      // Include surveyGroupId if available
      const surveyGroupId = getSurveyGroupId();
      if (surveyGroupId) {
        headers.set("X-Survey-Group-ID", surveyGroupId);
      }

      return headers;
    },
  }),
  endpoints: (builder) => ({
    loginWithPasscode: builder.mutation<
      InteractiveSurveyLoginResponse,
      InteractiveSurveyLoginRequest
    >({
      query: (credentials) => ({
        url: "login",
        method: "POST",
        body: credentials,
      }),
    }),
    loginWithGroupPasscode: builder.mutation<
      InteractiveSurveyGroupLoginResponse,
      InteractiveSurveyGroupLoginRequest
    >({
      query: (credentials) => ({
        url: "group/login",
        method: "POST",
        body: credentials,
      }),
    }),
  }),
});

export const { useLoginWithPasscodeMutation, useLoginWithGroupPasscodeMutation } =
  interactiveSurveyAuth;

/**
 * Token management for interactive survey authentication
 */
export const saveInteractiveToken = (token: string, surveyId: string) => {
  localStorage.setItem("interactiveToken", token);
  localStorage.setItem("surveyId", surveyId);
  // Clear any stale group-scoped ID so it isn't sent alongside this token
  localStorage.removeItem("surveyGroupId");
};

export const saveInteractiveGroupToken = (
  token: string,
  surveyGroupId: string,
) => {
  localStorage.setItem("interactiveToken", token);
  localStorage.setItem("surveyGroupId", surveyGroupId);
  // Clear any stale single-survey ID so it isn't sent alongside this token
  localStorage.removeItem("surveyId");
};

export const getInteractiveToken = (): string | null => {
  return localStorage.getItem("interactiveToken");
};

export const getSurveyId = (): string | null => {
  return localStorage.getItem("surveyId");
};

// Switches the "surveyId" header context without touching the token or
// surveyGroupId — used when navigating between a group's member surveys.
export const setActiveSurveyId = (surveyId: string) => {
  localStorage.setItem("surveyId", surveyId);
};

export const clearActiveSurveyId = () => {
  localStorage.removeItem("surveyId");
};

export const getSurveyGroupId = (): string | null => {
  return localStorage.getItem("surveyGroupId");
};


export const clearInteractiveAuth = () => {
  localStorage.removeItem("interactiveToken");
  localStorage.removeItem("surveyId");
  localStorage.removeItem("surveyGroupId");
};
